import { Button } from '@heidi/design-system/button';

import {
  isSessionsSelectedAllAtom,
  selectedSessionIdsAtom,
  useSessionsList,
} from '@/components/layout/Navigation/sessions/side-nav-list/hooks/useSessionsList';

import { LinkSessionsComboBoxResponsive } from '@/components/ui/responsive-combobox/link-sessions-responsive-combobox';
import { useSessionListLinkPopoverHost } from '@/composition/hosts/patients/patient-find-or-link/useSessionListLinkPopoverHost';
import { useIsFindOrLinkPatientEnabled } from '@/features/flags';
import { toSuggestedMatchCandidates } from '@/features/patient-find-or-link/adapters/to-suggested-match-candidates';
import { useIsMobile } from '@/hooks/useWindowDimensions';
import { SessionListLinkPopover } from '@heidi/patients';
import { useTranslations } from '@heidi/runtime/i18n';
import { useAtomValue } from 'jotai';

import { Check, Link as LucideLink, Trash2, Undo2 } from 'lucide-react';
import { useMemo } from 'react';
import { useSessionSearch } from '../../hooks/useSessionSearch';

export const SessionsListFooter = () => {
  const linkPopoverHost = useSessionListLinkPopoverHost();
  const { toggleSelectedAll, handleDeleteSelected } = useSessionsList();
  const isSessionsSelectedAll = useAtomValue(isSessionsSelectedAllAtom);
  const isFindOrLinkEnabled = useIsFindOrLinkPatientEnabled();

  const selectedSessionIds = useAtomValue(selectedSessionIdsAtom);
  const { sessionSearchResult } = useSessionSearch();

  const actionableSelectedSessionIds = useMemo(() => {
    return selectedSessionIds.filter((sessionId) =>
      sessionSearchResult?.some(
        (session) => session.session_id === sessionId && !session.is_offline_synthetic
      )
    );
  }, [selectedSessionIds, sessionSearchResult]);

  const canShowLinkSessionToPatient = useMemo(() => {
    // if selectedSessionIds includes id that in searchResult, and already linked to a patient, then can't show
    return !actionableSelectedSessionIds.some((sessionId) =>
      sessionSearchResult?.some(
        (session) => session.session_id === sessionId && session.patient_profile?.id
      )
    );
  }, [actionableSelectedSessionIds, sessionSearchResult]);
  const selectedSessionWithCandidates = useMemo(() => {
    if (actionableSelectedSessionIds.length !== 1) return undefined;

    return sessionSearchResult?.find(
      (session) => session.session_id === actionableSelectedSessionIds[0]
    );
  }, [actionableSelectedSessionIds, sessionSearchResult]);

  const { t } = useTranslations();

  const isMobile = useIsMobile();
  const showSessionsListFooter = useMemo(() => {
    return actionableSelectedSessionIds.length > 0 && !isMobile;
  }, [actionableSelectedSessionIds, isMobile]);

  if (!showSessionsListFooter) return <></>;

  const linkComboboxProps = {
    popoverAlign: 'start' as const,
    popoverSide: 'top' as const,
    popoverAlignOffset: -10,
    popoverSideOffset: 5,
    commandInputPlaceholder: t('sessions.searchForExistingPatient'),
    selectedSessionIds: actionableSelectedSessionIds,
    patientProfileAppointmentCandidates:
      selectedSessionWithCandidates?.patient_profile_appointment_candidates,
    className: 'w-[280px]',
    trigger: (
      <Button variant="outline" size="xs" className="w-full text-text-secondary">
        <LucideLink aria-hidden="true" />
        {t('sessions.linkSession', {
          count: actionableSelectedSessionIds.length,
        })}
      </Button>
    ),
  };

  return (
    <div className="flex flex-col gap-2">
      {canShowLinkSessionToPatient && (
        <div className="w-full">
          {isFindOrLinkEnabled ? (
            <SessionListLinkPopover
              {...linkComboboxProps}
              host={linkPopoverHost}
              patientProfileAppointmentCandidates={
                selectedSessionWithCandidates?.patient_profile_appointment_candidates
                  ? toSuggestedMatchCandidates(
                      selectedSessionWithCandidates.patient_profile_appointment_candidates
                    )
                  : undefined
              }
            />
          ) : (
            <LinkSessionsComboBoxResponsive {...linkComboboxProps} />
          )}
        </div>
      )}

      <Button
        variant="outline"
        size="xs"
        className="text-text-secondary"
        onClick={() => {
          toggleSelectedAll();
        }}
      >
        {isSessionsSelectedAll ? <Undo2 /> : <Check />}
        <span>
          {isSessionsSelectedAll ? t('sessions.deselectAll') : t('sessions.selectAll')}
        </span>
      </Button>

      <Button variant="destructive_soft" size="xs" onClick={handleDeleteSelected}>
        <Trash2 />
        {t('sessions.deleteSelected', { count: actionableSelectedSessionIds.length })}
      </Button>
    </div>
  );
};
